import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import classNames from 'classnames';
import PaginationEllipsis from '../../common/PaginationEllipsis';
import { list } from '../../../lib/api/archive';
import CardDropdown from '../../common/CardDropdown';
import { Edit } from './Edit';

const INITIAL_SEARCH = { full_name: '' };

export const List = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(INITIAL_SEARCH);
  const [searchInput, setSearchInput] = useState('');
  const [editArchiveGuid, setEditArchiveGuid] = useState(null);

  useEffect(() => {
    fetchData(search, page);
  }, [page]);

  const normalizeRows = response => {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  };

  const fetchData = async (activeSearch = INITIAL_SEARCH, activePage = 1) => {
    setLoading(true);

    try {
      const response = await list({
        page: activePage,
        pageSize: 10,
        ...(activeSearch.full_name?.trim()
          ? { full_name: activeSearch.full_name.trim() }
          : {})
      });

      const rows = normalizeRows(response);

      setItems(rows);
      setTotalPages(response?.totalPages || (rows.length ? 1 : 0));
    } catch (error) {
      console.error('Greska prilikom ucitavanja arhive:', error);
      setItems([]);
      setTotalPages(0);
    }

    setLoading(false);
  };

  const handlePageChange = newPage => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleSearchSubmit = async event => {
    event.preventDefault();

    const nextSearch = { full_name: searchInput };
    setSearch(nextSearch);
    setPage(1);
    await fetchData(nextSearch, 1);
  };

  const handleResetSearch = async () => {
    setSearch(INITIAL_SEARCH);
    setSearchInput('');
    setPage(1);
    await fetchData(INITIAL_SEARCH, 1);
  };

  const handleUpdateArchive = updatedArchive => {
    setItems(prev =>
      prev.map(item =>
        item.archive_guid === updatedArchive.archive_guid ? updatedArchive : item
      )
    );
  };

  const renderActions = item => (
    <CardDropdown>
      <div className="py-2">
        <Dropdown.Item onClick={() => setEditArchiveGuid(item.archive_guid)}>
          Izmjena
        </Dropdown.Item>
      </div>
    </CardDropdown>
  );

  return (
    <Row className="g-3">
      <Col xs={12}>
        <Card className={classNames('h-100')}>
          <Card.Header>
            <Row className="g-2">
              <Col xs={12}>
                <h5 className="mb-0">Arhiva</h5>
              </Col>
              <Col xs={12}>
                <Form onSubmit={handleSearchSubmit}>
                  <Row className="g-2 align-items-center mt-1">
                    <Col lg={8} md={7} sm={12}>
                      <Form.Control
                        type="text"
                        value={searchInput}
                        placeholder="Pretraga po imenu i prezimenu"
                        onChange={event => setSearchInput(event.target.value)}
                      />
                    </Col>
                    <Col lg="auto" md="auto" sm={6}>
                      <Button type="submit" variant="primary" className="w-100 px-4">
                        Pretrazi
                      </Button>
                    </Col>
                    <Col lg="auto" md="auto" sm={6}>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        className="w-100 px-4"
                        onClick={handleResetSearch}
                      >
                        Reset
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Col>
            </Row>
          </Card.Header>

          <Card.Body className="bg-body-tertiary">
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <>
                <div className="d-none d-xl-block">
                  <Table responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Ime i prezime</th>
                        <th>Datum rodjenja</th>
                        <th>Mjesto rodjenja</th>
                        <th>Klub registracije</th>
                        <th>Broj licence</th>
                        <th className="text-end">Akcije</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length > 0 ? (
                        items.map(item => (
                          <tr key={item.archive_guid || item.archive_id}>
                            <td>{item.serial_number || ''}</td>
                            <td>{item.full_name || ''}</td>
                            <td>{item.birth_date || ''}</td>
                            <td>{item.birth_place || ''}</td>
                            <td>{item.registering_club || ''}</td>
                            <td>{item.license_number || ''}</td>
                            <td className="text-end">{renderActions(item)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-4">
                            Nema podataka za prikaz.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-xl-none">
                  {items.length > 0 ? (
                    <Row className="g-3">
                      {items.map(item => (
                        <Col xs={12} key={item.archive_guid || item.archive_id}>
                          <div
                            className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                            style={{ borderColor: '#dfe3ee' }}
                          >
                            <div className="d-flex align-items-start justify-content-between gap-2">
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold text-dark text-truncate fs-8">
                                    {item.full_name || '-'}
                                  </div>
                                  <div className="flex-shrink-0">{renderActions(item)}</div>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  <span className="badge rounded-pill badge-subtle-primary">
                                    #{item.serial_number || '-'}
                                  </span>
                                  <span className="badge rounded-pill badge-subtle-info">
                                    {item.license_number || 'Bez licence'}
                                  </span>
                                </div>

                                <div className="text-600 fs-10 mt-2">
                                  {item.birth_date || '-'}
                                  {item.birth_place ? ` - ${item.birth_place}` : ''}
                                </div>

                                <div className="text-700 fs-10 mt-1">
                                  {item.registering_club || '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="text-center py-4">Nema podataka za prikaz.</div>
                  )}
                </div>
              </>
            )}
          </Card.Body>

          <Card.Footer>
            <PaginationEllipsis
              page={page}
              totalPages={Math.max(totalPages, 1)}
              onPageChange={handlePageChange}
            />
          </Card.Footer>

          {editArchiveGuid && (
            <Modal show onHide={() => setEditArchiveGuid(null)} size="lg">
              <Modal.Body>
                <Edit
                  archiveGuid={editArchiveGuid}
                  onClose={() => setEditArchiveGuid(null)}
                  onUpdate={handleUpdateArchive}
                />
              </Modal.Body>
            </Modal>
          )}
        </Card>
      </Col>
    </Row>
  );
};
